import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import axios from 'axios';
import { getCurrentApiEndpoints } from '../config/api';

interface RegistrationData {
  id?: number;
  // Step 1: Company Type
  company_type?: string;
  country?: string;
  // Step 2: Business Activity
  business_activity_code?: string;
  business_activity?: string;
  business_description?: string;
  
  // Step 3: Company Name
  company_name_armenian?: string;
  company_name_english?: string;
  company_name_russian?: string;
  
  // Step 4: Owners Information and Ownership Structure (merged)
  owners_data?: string;
  authorized_capital?: number;
  
  // Step 5: Charter
  decision_making?: string;
  charter_preferences?: string;
  
  // Step 6: Additional Services
  additional_services?: string;
  additional_comments?: string;
  preferred_communication?: string;
}

interface RegistrationContextType {
  currentStep: number;
  registrationData: RegistrationData;
  isLoading: boolean;
  error: string | null;
  setCurrentStep: (step: number) => void;
  updateRegistrationData: (data: Partial<RegistrationData>) => void;
  createNewRegistration: () => Promise<void>;
  saveCompanyType: (data: any) => Promise<void>;
  saveBusinessActivity: (data: any) => Promise<void>;
  saveCompanyName: (data: any) => Promise<void>;
  saveOwnersInfo: (data: any) => Promise<void>;
  uploadDocument: (file: File, ownerIndex: number, documentType: string) => Promise<void>;
  saveCharter: (data: any) => Promise<void>;
  saveAdditionalServices: (data: any) => Promise<void>;
  submitRegistration: () => Promise<void>;
  loadRegistration: (registrationId?: number) => Promise<void>;
}

const RegistrationContext = createContext<RegistrationContextType | undefined>(undefined);

export const useRegistration = () => {
  const context = useContext(RegistrationContext);
  if (context === undefined) {
    throw new Error('useRegistration must be used within a RegistrationProvider');
  }
  return context;
};

interface RegistrationProviderProps {
  children: ReactNode;
}

export const RegistrationProvider: React.FC<RegistrationProviderProps> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [registrationData, setRegistrationData] = useState<RegistrationData>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateRegistrationData = (data: Partial<RegistrationData>) => {
    setRegistrationData(prev => ({ ...prev, ...data }));
  };

  const createNewRegistration = async () => {
    try {
      const endpoints = getCurrentApiEndpoints();
      const token = localStorage.getItem('token');
      await axios.post(endpoints.CREATE_NEW_REGISTRATION, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Failed to create new registration:', error);
    }
    setRegistrationData({});
    setCurrentStep(1);
  };

  const saveCompanyType = async (data: any) => {
    const endpoints = getCurrentApiEndpoints();
    const token = localStorage.getItem('token');
    await axios.post(endpoints.COMPANY_TYPE, data, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    updateRegistrationData(data);
  };

  const saveBusinessActivity = async (data: any) => {
    const endpoints = getCurrentApiEndpoints();
    const token = localStorage.getItem('token');
    await axios.post(endpoints.BUSINESS_ACTIVITY, data, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    updateRegistrationData(data);
  };

  const saveCompanyName = async (data: any) => {
    const endpoints = getCurrentApiEndpoints();
    const token = localStorage.getItem('token');
    await axios.post(endpoints.COMPANY_NAME, data, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    updateRegistrationData(data);
  };

  const saveOwnersInfo = async (data: any) => {
    const endpoints = getCurrentApiEndpoints();
    const token = localStorage.getItem('token');
    await axios.post(endpoints.OWNERS_INFO, data, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    updateRegistrationData(data);
  };

  const uploadDocument = async (file: File, ownerIndex: number, documentType: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('owner_index', ownerIndex.toString());
    formData.append('document_type', documentType);
    const endpoints = getCurrentApiEndpoints();
    const token = localStorage.getItem('token');
    await axios.post(endpoints.UPLOAD_DOCUMENT, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      },
    });
  };

  const saveCharter = async (data: any) => {
    const endpoints = getCurrentApiEndpoints();
    const token = localStorage.getItem('token');
    await axios.post(endpoints.CHARTER, data, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    updateRegistrationData(data);
  };

  const saveAdditionalServices = async (data: any) => {
    const endpoints = getCurrentApiEndpoints();
    const token = localStorage.getItem('token');
    await axios.post(endpoints.ADDITIONAL_SERVICES, data, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    updateRegistrationData(data);
  };

  const submitRegistration = async () => {
    const endpoints = getCurrentApiEndpoints();
    const token = localStorage.getItem('token');
    await axios.post(endpoints.SUBMIT, {}, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  };

  const loadRegistration = useCallback(async (registrationId?: number) => {
    setIsLoading(true);
    setError(null);
    try {
      let url = registrationId 
        ? getCurrentApiEndpoints().REGISTRATION_BY_ID(registrationId)
        : getCurrentApiEndpoints().REGISTRATION;
      const token = localStorage.getItem('token');
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setRegistrationData(response.data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.log('No existing draft registration found');
        setRegistrationData({});
      } else {
        setError('Failed to load registration: ' + error.message);
        console.error('Failed to load registration:', error);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <RegistrationContext.Provider value={{
      currentStep,
      registrationData,
      isLoading,
      error,
      setCurrentStep,
      updateRegistrationData,
      createNewRegistration,
      saveCompanyType,
      saveBusinessActivity,
      saveCompanyName,
      saveOwnersInfo,
      uploadDocument,
      saveCharter,
      saveAdditionalServices,
      submitRegistration,
      loadRegistration,
    }}>
      {children}
    </RegistrationContext.Provider>
  );
};